import axios from "axios";
import { useAxiosHook } from "./useAxiosHook";
import { useDispatch } from "react-redux";
import { cachedDataItemSet } from "../redux/actions/cachedDataActions";
import { CACHED_DATA_REDUCER_TYPES } from "../redux/reducers/cachedDataReducer";
import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";
import { useCallback } from "react";
import { isArray, size } from "lodash";

const ROUTE_PREFIX = "borrower_portal_admin";

export const useBorrowerPortalHook = () => {
  const {
    axiosPostIsLoading,
    isLoading,
    axiosGetIsLoading,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
  } = useAxiosHook();

  const loading = isLoading || axiosGetIsLoading || axiosPostIsLoading;
  const borrowerPortalHookLoading = loading;
  const error = axiosError;
  const dispatch = useDispatch();

  const getBorrowerPortalUsers = async ({
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/all_borrower_portal_users`;
    const source = axios.CancelToken.source();
    const CACHED_DATA_KEY = CACHED_DATA_REDUCER_TYPES.BORROWER_PORTAL_USERS;

    await axiosGetRequest({
      name: "Get Borrower Portal Users",
      path: endpoint,
      cancelSource: source,
      onSuccessFn: (d) => {
        dispatch(cachedDataItemSet(CACHED_DATA_KEY, d));
        if (onSuccessFn) {
          onSuccessFn(d);
        }
      },
      onCompleteFn,
      onFailFn,
    });
  };

  const getBorrowerProfile = async ({
    _id,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ _id });

    const endpoint = `${ROUTE_PREFIX}/get_borrower_profile/${_id}`;

    await axiosGetRequest({
      name: "Get Borrower Profile",
      path: endpoint,

      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const getStagedBorrowerByEmail = async ({
    email,
    onSuccessFn,
    cancelSource: externalCancelSource,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ email });

    const endpoint = `${ROUTE_PREFIX}/get_staged_borrower_profile_by_email/${email}`;
    const source = externalCancelSource || axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get staged Borrower By Email",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const getBorrowerProfileWithDeals = async ({
    _id,
    cancelSource: externalCancelSource,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ _id });
    const endpoint = `${ROUTE_PREFIX}/borrower_portal_user_with_deals/${_id}`;
    const source = externalCancelSource || axios.CancelToken.source();

    await axiosGetRequest({
      name: "Get Borrower Profile With Deals",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const updateBorrowerProfile = async ({
    _id,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/update_borrower_profile/${_id}`;
    const source = axios.CancelToken.source();

    validateRequiredFields({ data, _id });

    await axiosPostRequest({
      name: "Update Borrower Profile",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn: (d) => {
        if (onSuccessFn) {
          onSuccessFn(d);
        }
      },
      onSuccessMsg: "Borrower updated successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const uploadBorrowerProfileDocuments = async ({
    _id,
    files,
    onSuccessFn,
    onSuccessMsg = "Files Uploaded",
    onFailFn,
  }) => {
    validateRequiredFields({ files, _id });
    const endpoint = `${ROUTE_PREFIX}/upload_borrower_docs/${_id}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Upload Borrower Profile Docs",
      path: endpoint,
      files,
      formData: true,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onFailFn,
    });
  };

  const updateBorrowerDocMetadata = async ({
    borrowerId,
    files,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, files });

    const endpoint = `${ROUTE_PREFIX}/update_borrower_doc_metadata/${borrowerId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Borrower Doc Metadata",
      path: endpoint,
      payload: files,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const getBorrowerProfileDocumentById = useCallback(
    async ({
      borrowerId,
      file_storage_name,
      isFile = true,
      fileDownloadName,
      onSuccessFn,
      fileDownload,
      openInNewTab,
      onFailFn,
    }) => {
      validateRequiredFields({ file_storage_name, borrowerId });
      const endpoint = `${ROUTE_PREFIX}/borrower_file_by_id/${borrowerId}/${file_storage_name}`;
      const source = axios.CancelToken.source();

      await axiosGetRequest({
        name: "Get Borrower Profile Document By Id",
        path: endpoint,
        cancelSource: source,
        isFile,
        fileDownloadName,
        fileDownload,
        openInNewTab,
        onSuccessFn,
        onFailFn,
      });
    },
    [axiosGetRequest],
  );

  const createEntityWithUpload = async ({
    borrowerId,
    files,
    onSuccessFn,
    onSuccessMsg = "Entity Created",
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, files });

    const endpoint = `${ROUTE_PREFIX}/create_entity_with_upload/${borrowerId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Entity With Upload",
      path: endpoint,
      files,
      formData: true,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg,
      onFailFn,
    });
  };

  const createEntity = async ({
    borrowerId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, data });

    const endpoint = `${ROUTE_PREFIX}/create_entity/${borrowerId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Entity",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Entity added successfully",
      onFailFn,
    });
  };

  const createEntityWithOrWithoutFiles = async ({
    borrowerId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, data });
    const endpoint_FILES = `${ROUTE_PREFIX}/create_entity_with_upload/${borrowerId}`;
    const endpoint_NO_FILES = `${ROUTE_PREFIX}/create_entity/${borrowerId}`;
    const source = axios.CancelToken.source();

    if (isArray(data?.uploadMetadata) && size(data?.uploadMetadata) >= 1) {
      // console.log("With Files", data);
      // return;
      await axiosPostRequest({
        name: "Create Entity With Files",
        payload: data,
        path: endpoint_FILES,
        formData: true,
        cancelSource: source,
        onSuccessFn,
        onSuccessMsg: "Entity added successfully",
        onCompleteFn,
        onFailFn,
      });
    } else {
      // console.log("Without Files", data);
      // return;
      await axiosPostRequest({
        name: "Create Entity Without Files",
        payload: data,
        path: endpoint_NO_FILES,
        cancelSource: source,
        onSuccessMsg: "Entity added successfully",
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    }
  };

  const updateEntity = async ({
    borrowerId,
    entityId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, entityId, data });

    const endpoint = `${ROUTE_PREFIX}/update_entity/${borrowerId}/${entityId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Entity",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Entity updated successfully",
      onFailFn,
    });
  };

  const createExperienceRecord = async ({
    borrowerId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, data });

    const endpoint = `${ROUTE_PREFIX}/create_experience_record/${borrowerId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Experience Record",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Experience record created successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const updateExperienceRecord = async ({
    borrowerId,
    recordId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, recordId, data });

    const endpoint = `${ROUTE_PREFIX}/update_experience_record/${borrowerId}/${recordId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Experience Record",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Experience record updated successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const addBankAccount = async ({
    borrowerId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, data });

    const endpoint = `${ROUTE_PREFIX}/add_bank_account/${borrowerId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Add Bank Account",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Bank account added successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const updateBankAccount = async ({
    borrowerId,
    _id,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, _id, data });

    const endpoint = `${ROUTE_PREFIX}/update_bank_account/${borrowerId}/${_id}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Update Bank Account",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Bank account updated successfully",
      onFailFn,
    });
  };

  const searchBorrowerByEmail = async ({
    email,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ email });

    const endpoint = `${ROUTE_PREFIX}/search_user_by_email/${email}`;
    const source = axios.CancelToken.source();

    await axiosGetRequest({
      name: "Search Borrower Profile By Email",
      path: endpoint,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    });
  };

  const createBorrowerProfileStaging = async ({
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ data });

    const endpoint = `${ROUTE_PREFIX}/create_borrower_profile_staging`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Borrower Profile Staging",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onCompleteFn,
      onSuccessMsg: "Email Sent to Borrower",
      onFailFn,
    });
  };

  const getAllStagedBorrowers = async ({
    salespersonEmail,
    activeStatus,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    const endpoint = `${ROUTE_PREFIX}/get_all_staged_borrower_profiles`;
    const source = axios.CancelToken.source();
    const params = new URLSearchParams();
    if (salespersonEmail) params.append("salespersonEmail", salespersonEmail);
    if (activeStatus) params.append("activeStatus", activeStatus);
    const queryString = params.toString();

    await axiosGetRequest({
      name: "Get All Staged Borrowers",
      path: queryString ? `${endpoint}?${queryString}` : endpoint,
      cancelSource: source,
      onSuccessFn: (d) => {
        dispatch(
          cachedDataItemSet(
            CACHED_DATA_REDUCER_TYPES.BORROWER_PORTAL_USERS_STAGED,
            d,
          ),
        );
        if (onSuccessFn) {
          onSuccessFn(d);
        }
      },
      onCompleteFn,
      onFailFn,
    });
  };

  const createBorrowerProfileFromStaging = async ({
    emailAddress,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ emailAddress });

    const endpoint = `${ROUTE_PREFIX}/create_borrower_profile_from_staging`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Borrower Profile From Staging",
      path: endpoint,
      payload: { emailAddress },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Borrower profile created from staging",
      onCompleteFn,
      onFailFn,
    });
  };

  const resendBorrowerProfileInvite = async ({
    emailAddress,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ emailAddress });

    const endpoint = `${ROUTE_PREFIX}/resend_borrower_profile_invite`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Resend Borrower Profile Invite",
      path: endpoint,
      payload: { emailAddress },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Invite resent successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const startStagedApplicationAndInvite = async ({
    emailAddress,
    applicationData,
    sendInvite = true,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ emailAddress, applicationData });

    const endpoint = `${ROUTE_PREFIX}/start_staged_application_and_invite`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Start Staged Application And Invite",
      path: endpoint,
      payload: {
        emailAddress,
        applicationData,
        sendInvite,
        ...data,
      },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Staged application created",
      onCompleteFn,
      onFailFn,
    });
  };


  const deleteBorrowerUserAccount = async ({
    emailAddress,

    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ emailAddress });

    const endpoint = `${ROUTE_PREFIX}/delete_borrower_user_account`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Delete Borrower User Account",
      path: endpoint,
      payload: {
        emailAddress,
      },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Borrower user account deleted successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const deleteStagedBorrowerUserAccount = async ({
    emailAddress,

    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ emailAddress });

    const endpoint = `${ROUTE_PREFIX}/delete_staged_borrower_user`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Delete Staged Borrower User Account",
      path: endpoint,
      payload: {
        emailAddress,
      },
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Staged borrower user account deleted successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  const createBorrowerTermsSheetForExistingDeal = async ({
    borrowerId,
    dealId,
    data,
    onSuccessFn,
    onCompleteFn,
    onFailFn,
  }) => {
    validateRequiredFields({ borrowerId, dealId, data });

    const endpoint = `${ROUTE_PREFIX}/create_letter_of_intent_existing_deal/${dealId}/${borrowerId}`;
    const source = axios.CancelToken.source();

    await axiosPostRequest({
      name: "Create Borrower Terms Sheet",
      path: endpoint,
      payload: data,
      cancelSource: source,
      onSuccessFn,
      onSuccessMsg: "Borrower terms sheet created successfully",
      onCompleteFn,
      onFailFn,
    });
  };

  return {
    borrowerPortalHookLoading,
    loading,
    error,

    getBorrowerPortalUsers,
    getBorrowerProfile,
    getBorrowerProfileWithDeals,
    updateBorrowerProfile,
    uploadBorrowerProfileDocuments,
    updateBorrowerDocMetadata,
    getBorrowerProfileDocumentById,
    createEntityWithUpload,
    createEntity,
    createEntityWithOrWithoutFiles,
    updateEntity,
    createExperienceRecord,
    updateExperienceRecord,
    addBankAccount,
    updateBankAccount,
    searchBorrowerByEmail,
    getStagedBorrowerByEmail,
    createBorrowerProfileStaging,
    getAllStagedBorrowers,
    createBorrowerProfileFromStaging,
    resendBorrowerProfileInvite,
    startStagedApplicationAndInvite,
    deleteBorrowerUserAccount,
    deleteStagedBorrowerUserAccount,
    createBorrowerTermsSheetForExistingDeal,
  };
};


